import React, { useEffect, useMemo, useRef } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Calendar, Tag, ChevronLeft, Clock, Zap } from 'lucide-react';
import DOMPurify from 'dompurify';
import wpPosts from '../data/blogPosts.json';
import { getGeneratedArticles } from '../data/blogLoader';
import { autoLinkCalculators } from '../utils/autoLinkCalculators';

// Allowlist for blog HTML — inline scripts, iframes, event handlers, javascript: URIs are stripped.
const SANITIZER_CONFIG = {
  ALLOWED_TAGS: [
    'p', 'br', 'strong', 'em', 'b', 'i', 'u', 's', 'sub', 'sup', 'small', 'mark',
    'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
    'ul', 'ol', 'li',
    'blockquote', 'q', 'cite',
    'a', 'img', 'figure', 'figcaption',
    'table', 'thead', 'tbody', 'tfoot', 'tr', 'th', 'td', 'caption', 'colgroup', 'col',
    'code', 'pre', 'kbd', 'samp', 'var',
    'hr', 'div', 'span', 'section', 'article', 'aside',
    'dl', 'dt', 'dd',
  ],
  ALLOWED_ATTR: ['href', 'src', 'alt', 'title', 'width', 'height', 'class', 'target', 'rel', 'loading', 'colspan', 'rowspan', 'scope'],
  ALLOWED_URI_REGEXP: /^(?:https?:|mailto:|tel:|\/|#)/i,
  FORBID_TAGS: ['script', 'style', 'iframe', 'object', 'embed', 'form', 'input', 'button', 'link', 'meta', 'base'],
  FORBID_ATTR: ['onerror', 'onload', 'onclick', 'onmouseover', 'onfocus', 'onblur', 'onchange', 'onsubmit', 'style'],
  ADD_ATTR: ['target'],
};

function sanitize(html) {
  return DOMPurify.sanitize(html || '', SANITIZER_CONFIG);
}

export default function BlogPostPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const contentRef = useRef(null);

  const { post, related } = useMemo(() => {
    const gen = getGeneratedArticles();
    const all = [...gen, ...wpPosts];
    const post = all.find(p => p.slug === slug);
    const related = post
      ? all
          .filter(p => p.slug !== slug && (p.categories || []).some(c => (post.categories || []).includes(c)))
          .slice(0, 4)
      : [];
    return { post, related };
  }, [slug]);

  useEffect(() => { window.scrollTo(0, 0); }, [slug]);

  // White backdrop on article pages
  useEffect(() => {
    document.body.classList.add('blog-article-page');
    return () => document.body.classList.remove('blog-article-page');
  }, []);

  // Auto-link calculator names inside article content after mount / content change
  useEffect(() => {
    if (post && contentRef.current) {
      // Run once after HTML is injected
      autoLinkCalculators(contentRef.current);
      // Harden external links: force rel="noopener noreferrer" + target="_blank"
      contentRef.current.querySelectorAll('a[href]').forEach(a => {
        const href = a.getAttribute('href') || '';
        if (/^https?:\/\//i.test(href)) {
          a.setAttribute('rel', 'noopener noreferrer nofollow');
          a.setAttribute('target', '_blank');
        }
      });
      // Intercept clicks on inserted anchors → SPA navigation
      const handler = (e) => {
        const a = e.target.closest('a.calc-inline-link');
        if (!a) return;
        const href = a.getAttribute('href');
        if (href && href.startsWith('/')) {
          e.preventDefault();
          navigate(href);
        }
      };
      const el = contentRef.current;
      el.addEventListener('click', handler);
      return () => el.removeEventListener('click', handler);
    }
  }, [post, navigate]);

  // Update meta tags for SEO
  useEffect(() => {
    if (!post) return;
    const title = `${post.title} | PocketCalculator.net`;
    document.title = title;
    const setMeta = (name, content, attr = 'name') => {
      let el = document.querySelector(`meta[${attr}="${name}"]`);
      if (!el) { el = document.createElement('meta'); el.setAttribute(attr, name); document.head.appendChild(el); }
      el.setAttribute('content', content);
    };
    setMeta('description', post.excerpt || '');
    setMeta('og:title', post.title, 'property');
    setMeta('og:description', post.excerpt || '', 'property');
    setMeta('og:type', 'article', 'property');
    // Clear og:image between posts so a stale image doesn't leak
    if (post.featured) {
      setMeta('og:image', post.featured, 'property');
    } else {
      document.querySelector('meta[property="og:image"]')?.remove();
    }
    // Canonical link
    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) { canonical = document.createElement('link'); canonical.setAttribute('rel', 'canonical'); document.head.appendChild(canonical); }
    canonical.setAttribute('href', `https://pocketcalculator.net/blog/${post.slug}`);
    return () => { document.title = 'PocketCalculator.net'; };
  }, [post]);

  if (!post) {
    return (
      <div className="max-w-3xl mx-auto text-center py-16">
        <h1 className="text-2xl font-bold text-slate-900 mb-2">Article Not Found</h1>
        <p className="text-slate-600 mb-6">The article you're looking for doesn't exist.</p>
        <button onClick={() => navigate('/blog')} className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">
          Browse All Articles
        </button>
      </div>
    );
  }

  const readMin = Math.max(1, Math.round((post.wordCount || 1000) / 220));

  return (
    <article className="max-w-4xl mx-auto bg-white rounded-2xl border border-slate-200 shadow-sm px-5 sm:px-10 py-8 sm:py-10" data-testid="blog-post-page">
      <Link to="/blog" className="inline-flex items-center gap-1 text-sm text-blue-600 hover:underline mb-4">
        <ChevronLeft className="w-4 h-4" /> All Articles
      </Link>

      {/* Header */}
      <header className="mb-6">
        {post.categories && post.categories[0] && (
          <div className="flex items-center gap-1 text-xs text-blue-600 font-semibold mb-3 uppercase tracking-wide">
            <Tag className="w-3 h-3" /> {post.categories[0]}
          </div>
        )}
        <h1 className="text-3xl sm:text-4xl font-bold text-slate-900 leading-tight mb-4">{post.title}</h1>
        <div className="flex flex-wrap items-center gap-4 text-sm text-slate-500 border-b border-slate-200 pb-4">
          <span className="flex items-center gap-1"><Calendar className="w-4 h-4" /> {(post.date || '').slice(0, 10)}</span>
          <span className="flex items-center gap-1"><Clock className="w-4 h-4" /> {readMin} min read</span>
          {post.wordCount ? <span>{post.wordCount.toLocaleString()} words</span> : null}
        </div>
      </header>

      {/* AI 30-second summary */}
      {post.summary && (
        <aside
          className="mb-8 rounded-xl bg-gradient-to-br from-blue-50 to-indigo-50 border border-blue-200 p-5 sm:p-6"
          data-testid="blog-post-summary"
        >
          <div className="flex items-center gap-2 mb-2">
            <div className="w-7 h-7 rounded-lg bg-blue-600 flex items-center justify-center flex-shrink-0">
              <Zap className="w-4 h-4 text-white" />
            </div>
            <div className="text-xs font-bold text-blue-700 uppercase tracking-wide">30-Second Summary</div>
          </div>
          <p className="text-[15px] text-slate-800 leading-relaxed">{post.summary}</p>
        </aside>
      )}

      {post.featured && (
        <div className="mb-6 rounded-xl overflow-hidden border border-slate-200">
          <img src={post.featured} alt={post.title} className="w-full h-auto" />
        </div>
      )}

      {/* Content — sanitized before injection. key=slug forces React to unmount so autoLink can re-run. */}
      <div
        key={slug}
        ref={contentRef}
        className="blog-content prose prose-slate max-w-none"
        data-testid="blog-post-content"
        dangerouslySetInnerHTML={{ __html: sanitize(post.content) }}
      />

      {/* Tags */}
      {post.tags && post.tags.length > 0 && (
        <div className="mt-8 pt-6 border-t border-slate-200 flex flex-wrap gap-2">
          {post.tags.map(t => (
            <span key={t} className="text-xs px-3 py-1 bg-slate-100 text-slate-700 rounded-full">#{t}</span>
          ))}
        </div>
      )}

      {/* Related */}
      {related.length > 0 && (
        <section className="mt-12 pt-8 border-t border-slate-200">
          <h2 className="text-xl font-bold text-slate-900 mb-4">Related Articles</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {related.map(r => (
              <Link key={r.slug} to={`/blog/${r.slug}`} className="block p-4 bg-white border border-slate-200 rounded-lg hover:border-blue-400 hover:shadow-sm transition-all">
                <div className="font-semibold text-slate-900 mb-1 line-clamp-2">{r.title}</div>
                <div className="text-xs text-slate-500">{(r.date || '').slice(0, 10)}</div>
              </Link>
            ))}
          </div>
        </section>
      )}
    </article>
  );
}
